import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { RecordFilter } from '@/types/api';
import { isEmptyContent } from '@/utils/editor';
import useAi from '@/components/Record/hooks/useAi';
import {
  useAiSummeryResult,
  useTalkFlowDocumentAiSummary,
} from '@/hooks/apiHooks';
import { ContentsRef } from '@/components/Record/TalkFLow/Contents';

/**
 * 재접속(스토리지 무관) 본문 복구.
 *
 * AI 정리가 오프라인(창 닫힘/다른 기기) 중에 완료된 경우, 문서를 다시 열었을 때
 * 완료된 최신 정리 결과를 본문(content)에 자동 반영한다.
 *
 * - 서버 기준: AI_PIPELINE_RESULT_DETAIL 최신 항목의 status==='success' 를 완료로 판단.
 *   (localStorage pending 에 의존하지 않으므로 다른 컴퓨터/폰에서도 동작)
 * - 사용자 작성 보호: 본문이 "비어있을 때만" 채운다(덮어쓰지 않음).
 * - 추천 제목은 문서 응답의 suggestedSubject를 화면에서 fallback으로 표시하므로
 *   실제 subject 폼 값에는 복사하지 않는다.
 * - 실시간 완료는 onCompleted(handleAiOnCreate) 가 담당하며, 본 훅은 본문이 비어있을 때만
 *   1회 동작하므로 그 경로와 겹쳐도 안전(멱등)하다.
 *
 * useTalkFlowDocumentAiSummary / useTalkFlowDocument 는 SWR 캐시 공유 → 추가 요청 없음.
 */
export default function useRecoverOrganizeContent(
  contentsRef: React.RefObject<ContentsRef | null>,
  docId: string,
) {
  const { isOwner } = useAi();
  const { getValues } = useFormContext<RecordFilter>();
  const { passportId } = useAtomValue(MyInfoState);
  const { data: list } = useTalkFlowDocumentAiSummary({
    documentId: docId,
    passportId,
  });

  // docId 변경(모달 재사용) 시 1회 가드 초기화
  const doneRef = useRef(false);
  useEffect(() => {
    doneRef.current = false;
  }, [docId]);

  useEffect(() => {
    if (doneRef.current || !isOwner) return;
    if (!list || list.length === 0) return;

    // 본문이 이미 있으면 복구하지 않음(사용자 작성 보호). 이후 재시도도 막는다.
    if (!isEmptyContent(getValues('content'))) {
      doneRef.current = true;
      return;
    }

    // createdAt 최신 1건이 완료(success)면 그 결과로 채운다. (진행중/실패/미확정이면 대기)
    const latest = [...list].sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    )[list.length - 1];
    const completed = latest?.status === 'success' || Boolean(latest?.content);
    if (!latest || !completed) return;

    let cancelled = false;

    (async () => {
      // 리스트에 content 가 있으면 사용하고, 없으면 결과를 조회한다.
      let text = latest.content ?? '';
      if (!text) {
        try {
          const res = await useAiSummeryResult(latest.pipelineId);
          text = res?.result?.summarize_text ?? '';
        } catch {
          return;
        }
      }
      if (cancelled || !text) return;

      // 조회하는 사이 본문이 채워졌으면(라이브 onCompleted 등) 스킵
      if (!isEmptyContent(getValues('content'))) {
        doneRef.current = true;
        return;
      }

      // 본문 반영 (handleAiOnCreate 가 ensureLexicalHtml 로 HTML 보장)
      contentsRef.current?.handleAiOnCreate?.(text);

      doneRef.current = true;
    })();

    return () => {
      cancelled = true;
    };
  }, [list, isOwner, docId, getValues, contentsRef]);
}
